/**
 * Derivation layer: turn stored state into everything the screens render.
 *
 * Nothing here is cached or persisted. Scores in, leaderboards out — which is
 * what makes a corrected handicap or stroke index repair the whole tournament
 * rather than leaving stale numbers behind.
 */

import { runMatch } from './engine/formats'
import { ballFor } from './engine/scoring'
import { allocationsFor, courseHandicapFor, findTee, holesFor, playingIndex } from './engine/handicap'
import {
  assignFlights,
  computeStandings,
  rankByFlightFinish,
  type Standings,
} from './engine/leaderboard'
import { payoutForPlace } from './engine/rank'
import type { TiebreakContext } from './engine/rank'
import { matchNameIn } from './engine/naming'
import { matchCounts } from './engine/types'
import type {
  Course,
  HoleScore,
  Match,
  Pairing,
  PlayerId,
  Round,
  RoundContext,
  RoundHandicap,
  Settlement,
  Tournament,
  UnitTab,
} from './engine/types'

export function courseOf(t: Tournament, round: Round): Course | undefined {
  return t.courses.find((c) => c.id === round.courseId)
}

/**
 * Assemble the context a format module needs for one round.
 *
 * Handicaps come from the round's frozen snapshot when one exists, and are
 * derived on the fly when it does not — so a round that has not started yet
 * still shows correct pops on the card.
 *
 * THE CONVENTION: a RoundHandicap's `courseHandicap` is the figure for the
 * holes THAT ROUND PLAYS, not an eighteen-hole figure. A nine is already halved
 * by the time it is stored, so nothing downstream may halve it again — that is
 * what handed a man eleven pops against a target that had taken only six off
 * him. The row is keyed on a round and the pops below are cut from this same
 * number, so any other reading would put the badges on the card at odds with
 * the target on the board.
 *
 * (The eighteen-hole figure still exists where it belongs: the pairing
 * optimizer and the roster both ask `courseHandicapFor` for all eighteen holes,
 * because neither is talking about a particular round.)
 */
export function buildContext(
  t: Tournament,
  round: Round,
  match: Match,
  scores: HoleScore[],
): RoundContext | null {
  const course = courseOf(t, round)
  if (!course) return null

  const holes = holesFor(course, round.nine)
  const groups = t.groups.filter((g) => g.roundId === round.id)
  const playerIds = groups.flatMap((g) => g.playerIds)

  const handicaps: RoundContext['handicaps'] = {}
  for (const pid of playerIds) {
    const frozen = t.handicaps.find((h) => h.roundId === round.id && h.playerId === pid)
    if (frozen) {
      handicaps[pid] = frozen
      continue
    }
    const player = t.players.find((p) => p.id === pid)
    if (!player) continue
    const tee = findTee(course, player)
    handicaps[pid] = {
      roundId: round.id,
      playerId: pid,
      teeSetId: tee.id,
      // The index frozen into the round is the one he PLAYED off, not
      // whichever of the two happens to be his current GHIN number. A round
      // recorded against the other one is a round nobody can reconstruct.
      handicapIndex: playingIndex(player),
      courseHandicap: courseHandicapFor(playingIndex(player), tee, holes),
      source: 'derived',
      quotaTarget: null,
    }
  }

  // Head-to-head formats allocate off the low man; stroke play uses the full
  // handicap, since its whole scoring model assumes it.
  const basis =
    match.config.kind === 'quota' || match.config.kind === 'strokeNet'
      ? 'full'
      : match.config.netBasis

  const pops: RoundContext['pops'] = {}
  if (match.config.kind === 'quota' || match.config.kind === 'strokeNet') {
    /*
     * Neither of these formats has pop holes, and neither may draw any.
     *
     * In both, the handicap is spent ONCE, up front — as the points you start on
     * in a quota, and as the number you start under par in the championship — and
     * then the day is played gross against that. Allocating it a second time
     * across the holes put a red dot on a hundred and twenty-seven squares of the
     * scorecard, each captioned "1 stroke", for strokes nobody receives.
     *
     * For a quota it changed no result, because quota scores off gross and never
     * reads these; it only described a format that was not being played, and put
     * an argument on the 6th green into somebody's hand.
     *
     * The championship was different, and worse: `tabulateStrokeNet` DID read
     * them, netting each hole against its own pop, and the per-hole cap was
     * computed off them too. So the card showed pop holes the group does not play
     * — a 10 handicap got ten of them at Sheep Ranch — and the cap they fed
     * quietly forgave blow-up holes. Confirmed with Scott, 2026-08-11: the
     * championship is stroke play off a starting figure, and a bad hole costs
     * full freight.
     *
     * Zeroed rather than skipped so every consumer still finds an array of the
     * right length where it expects one.
     */
    for (const id of playerIds) pops[id] = holes.map(() => 0)
  } else if (basis === 'relativeToLowInUnit') {
    // Each contest is its own handicap frame.
    for (const p of t.pairings.filter((x) => x.matchId === match.id)) {
      const members = [...p.sides[0].playerIds, ...p.sides[1].playerIds].map((id) => ({
        id,
        courseHandicap: handicaps[id]?.courseHandicap ?? 0,
      }))
      Object.assign(
        pops,
        allocationsFor(members, holes, 'relativeToLowInUnit', t.settings.handicapAllowance),
      )
    }
  } else if (basis === 'relativeToLowInGroup') {
    for (const g of groups) {
      const members = g.playerIds.map((id) => ({
        id,
        courseHandicap: handicaps[id]?.courseHandicap ?? 0,
      }))
      Object.assign(
        pops,
        allocationsFor(members, holes, 'relativeToLowInGroup', t.settings.handicapAllowance),
      )
    }
  } else {
    Object.assign(
      pops,
      allocationsFor(
        playerIds.map((id) => ({ id, courseHandicap: handicaps[id]?.courseHandicap ?? 0 })),
        holes,
        'full',
        1,
      ),
    )
  }

  const byPlayer: RoundContext['scores'] = {}
  for (const pid of playerIds) byPlayer[pid] = holes.map(() => undefined)
  for (const s of scores) {
    if (s.roundId !== round.id) continue
    const i = holes.findIndex((h) => h.number === s.holeNo)
    if (i >= 0 && byPlayer[s.playerId]) byPlayer[s.playerId]![i] = s.value
  }

  return {
    tournament: t,
    round,
    course,
    holes,
    groups,
    pairings: t.pairings.filter((p) => p.matchId === match.id),
    handicaps,
    pops,
    scores: byPlayer,
  }
}

/**
 * Write down what everybody is playing off, so it stops being a live opinion.
 *
 * Until this runs, `doc.handicaps` is empty and every allowance in the week is
 * derived from whatever `player.handicapIndex` says AT THE MOMENT OF RENDER. So
 * correcting somebody's index on Wednesday — the honest, ordinary thing to do
 * when a GHIN revision lands mid-trip — silently re-cut Sunday's pops, re-scored
 * Sunday's quota, and rewrote money that had already been signed for and settled.
 * Nobody would see it happen. The card would simply have different numbers on it
 * than it had the day it was signed.
 *
 * A snapshot is what fixes that, and it must be EXACTLY what `buildContext`
 * above would have derived — same tee, same index, same holes, same rounding.
 * Anything else and the act of freezing would itself move somebody's allowance,
 * which is a worse bug than the one being fixed. The two are deliberately next
 * to each other in this file, and `handicap-freeze.test.ts` asserts they agree
 * for every player in every round rather than trusting that they do.
 *
 * THE CONVENTION, restated because it is the easy thing to get wrong: a stored
 * `courseHandicap` is the figure for the holes THAT ROUND PLAYS. A nine is
 * already halved here, and nothing downstream may halve it again.
 *
 * Only players who are actually in a group for a round get a row, because those
 * are the only ones `buildContext` derives for. A round with no draw yet gets
 * nothing and keeps deriving live — which is correct, and is also why the
 * per-round re-freeze exists: change a tee or add a substitute after starting
 * and that round's snapshot is stale, so it has to be retaken deliberately.
 */
export function freezeHandicaps(t: Tournament, roundIds?: string[]): RoundHandicap[] {
  const wanted = roundIds ? new Set(roundIds) : null
  // Rows for rounds nobody asked about survive untouched — a re-freeze of
  // Tuesday must not disturb what Sunday was signed for.
  const kept = wanted ? t.handicaps.filter((h) => !wanted.has(h.roundId)) : []
  const fresh: RoundHandicap[] = []

  for (const round of t.rounds) {
    if (wanted && !wanted.has(round.id)) continue
    const course = courseOf(t, round)
    if (!course) continue
    const holes = holesFor(course, round.nine)
    const playerIds = t.groups
      .filter((g) => g.roundId === round.id)
      .flatMap((g) => g.playerIds)

    for (const pid of playerIds) {
      const player = t.players.find((p) => p.id === pid)
      if (!player) continue
      const tee = findTee(course, player)
      fresh.push({
        roundId: round.id,
        playerId: pid,
        teeSetId: tee.id,
        // The index he PLAYED off, which is not always his current GHIN number —
        // a man on his low is recorded against his low.
        handicapIndex: playingIndex(player),
        courseHandicap: courseHandicapFor(playingIndex(player), tee, holes),
        source: 'derived',
        quotaTarget: null,
      })
    }
  }

  return [...kept, ...fresh]
}

/** Which rounds have a stored snapshot, so the UI can say what is pinned. */
export function frozenRoundIds(t: Tournament): Set<string> {
  return new Set(t.handicaps.map((h) => h.roundId))
}

/**
 * Rounds whose snapshot no longer matches what the roster would produce today.
 *
 * NOT a warning, and the UI must not present it as one. A snapshot that differs
 * from the live roster is the snapshot doing its job — that is the entire point of
 * freezing, and for a round already played and signed for, differing is CORRECT.
 * This exists so that the one case where it is a problem can be seen: a tee
 * changed, or a substitute added, for a round not yet played.
 *
 * A row somebody typed by hand is never stale. `source: 'manual'` is a deliberate
 * override — flagging it would invite re-deriving it away, which is the opposite
 * of what its author wanted — so those players are skipped on both sides of the
 * comparison.
 */
export function staleFrozenRounds(t: Tournament): string[] {
  const out: string[] = []
  for (const roundId of frozenRoundIds(t)) {
    const stored = t.handicaps.filter((h) => h.roundId === roundId)
    const manual = new Set(stored.filter((h) => h.source === 'manual').map((h) => h.playerId))
    const key = (h: RoundHandicap): string =>
      `${h.playerId}:${h.teeSetId}:${h.handicapIndex}:${h.courseHandicap}`
    const now = freezeHandicaps(t, [roundId])
      .filter((h) => h.roundId === roundId && !manual.has(h.playerId))
      .map(key)
      .sort()
    const then = stored
      .filter((h) => !manual.has(h.playerId))
      .map(key)
      .sort()
    if (now.length !== then.length || now.some((k, i) => k !== then[i])) out.push(roundId)
  }
  return out
}

export interface TournamentView {
  byMatch: Record<string, { match: Match; round: Round; tabs: UnitTab[]; settlement: Settlement }>
  standings: Standings
  /**
   * The order the championship is flighted off, which is NOT `standings`.
   *
   * Same points, different tiebreak chain — the sheet breaks a seeding tie on
   * where you finished in an earlier round rather than on birdies. Exposed so
   * that anything showing the projected flights shows the numbers that actually
   * produced them; reading the flights from one table and the points from the
   * other is how a screen ends up contradicting itself over a place worth a
   * thousand dollars.
   */
  seeding: Standings
  flights: { flight: { name: string }; playerIds: string[] }[]
  /** Matches every group has signed for — fully settled, nothing still out. */
  finalMatchIds: Set<string>
  isFinal: (m: Match) => boolean
  /**
   * Whether every card the seeding depends on has been signed for.
   *
   * False means the flights shown are drawn from part of the week, so a screen
   * showing them owes the reader the reason and a way to fix it — see
   * `outstandingCards`. True means the seeding is settled and the flights are
   * the real draw rather than a projection.
   */
  seedingComplete: boolean
  /**
   * Whether THIS player's award in THIS match has settled into the ledger.
   *
   * The gate is per card, so "is this counted" stopped being a fact about the
   * match: your singles result is in while your opponent's foursome-mate three
   * groups back is not. The Board's breakdown rows ask this instead of
   * `finalMatchIds`, which is what had it telling a man his signed, settled
   * card was "not signed for".
   */
  counted: (matchId: string, playerId: PlayerId) => boolean
  /** Whether a foursome has signed for its card on a round. */
  signedCard: (roundId: string, groupId: string) => boolean
}

/**
 * Score the entire tournament from scratch. Cheap enough to run on every render.
 *
 * The championship is settled LAST and on its own, because it is flighted off
 * the standings and its own purse is part of those standings — score it in the
 * same pass as everything else and the seeding depends on what the seeding
 * decides. Settling it last also matches how the week actually works: a man is
 * flighted by where he stands walking to the first tee, not by what the flight
 * ends up paying him.
 */
export function computeAll(
  t: Tournament,
  scores: HoleScore[],
  /*
   * Which groups have signed for their card.
   *
   * Everything scores live, as it always did — the live board, the cards and
   * the projection all read the same numbers they read before. What changes is
   * that the LEDGER counts only signed cards: a foursome's own contests settle
   * when its card is signed, and a game against the field settles when the
   * round's last card is in — because before any gate existed, this paid the
   * championship purse out with the field thru 12 and ranked an unfinished
   * card as the best round on the property.
   */
  confirmed: { roundId: string; groupId: string }[] = [],
): TournamentView {
  const tb: TiebreakContext = { existing: t.tiebreaks, seed: t.settings.rngSeed }
  const activeIds = t.players.filter((p) => p.active).map((p) => p.id)
  const byMatch: TournamentView['byMatch'] = {}
  const settlements: Settlement[] = []
  const tabsByMatch: Record<string, UnitTab[]> = {}
  const matchSeq: Record<string, number> = {}

  const score = (match: Match, seeding?: Record<string, number>): void => {
    const round = t.rounds.find((r) => r.id === match.roundId)
    if (!round) return
    const ctx = buildContext(t, round, match, scores)
    if (!ctx) return
    const { tabs, settlement } = runMatch(ctx, match, tb, seeding)
    byMatch[match.id] = { match, round, tabs, settlement }
    /*
     * A round played for fun is carded and then let go.
     *
     * It keeps its tabs — the card, the hole outcomes, and who would have won —
     * because being scoreable is the entire reason somebody added it, and a
     * man who wins an evening nine should be able to see that he won it. What
     * it does not get is a settlement, which is what the standings and the
     * ledger are built from, or a place in the tiebreak counters, which are
     * what separate two men level on points.
     */
    if (!matchCounts(match)) return
    settlements.push(settlement)
    tabsByMatch[match.id] = tabs
    matchSeq[match.id] = round.seq
  }

  const signed = new Set(confirmed.map((c) => `${c.roundId}~${c.groupId}`))
  /*
   * A match is final only when EVERY group in its round has signed.
   *
   * Per group rather than per match because a match spans three foursomes and
   * one of them has no business settling another's money — but the award is a
   * whole-match thing (a quota field ranks all twelve), so it cannot be paid
   * until the last card is in.
   */
  const isFinal = (m: Match): boolean => {
    const gs = t.groups.filter((g) => g.roundId === m.roundId)
    return gs.length > 0 && gs.every((g) => signed.has(`${m.roundId}~${g.id}`))
  }

  /*
   * The championship is the FLIGHTED stroke-play match, not merely the first
   * stroke-play match found.
   *
   * An ad-hoc card is unflighted net stroke play — that is what a card IS — so
   * on the kind alone a nine somebody added on Tuesday evening could be picked
   * up as the championship, and the trophy scored off it. The flighting is what
   * makes the championship the championship.
   */
  const championship = t.matches.find((m) => m.config.kind === 'strokeNet' && m.config.flighted)
  for (const match of t.matches) if (match.id !== championship?.id) score(match)

  const finalIds = new Set(t.matches.filter(isFinal).map((m) => m.id))

  /*
   * ONE GATE FOR POINTS AND FOR MONEY — but the gate is the CARD, not the
   * match. Scott's revision of his own earlier rule, 2026-08-19.
   *
   * A signature is still the validation, and nothing unsigned counts for a
   * dollar or a point. What changed is the granularity: a game contested
   * inside the foursome (singles, the 2v2s, quota vs your group) is fully
   * decided by that foursome's own card, so it settles the moment that card
   * is signed — the standings no longer sit dark all afternoon because the
   * ten o'clock group finished three hours before the last one.
   *
   * A game against the FIELD cannot do this: one foursome's rank against all
   * twelve does not exist until the last card is in, so the field quota and
   * the championship keep the whole-match gate. The screens disclose the lag
   * either way — settled cards are in, and the standings are not final while
   * a match is still out.
   */
  const groupScoped = (m: Match): boolean =>
    !(m.config.kind === 'quota' && m.config.rankingScope === 'field') &&
    m.config.kind !== 'strokeNet'

  const cardSigned = (m: Match, groupId: string): boolean =>
    signed.has(`${m.roundId}~${groupId}`)

  /*
   * The settlements the ledger and the seeding may count, award by award.
   *
   * A fully signed match passes whole. A partly signed, group-scoped match
   * passes only the awards of players whose OWN card is signed and complete —
   * `complete` because a signature attests a finished card, and an award off
   * a live contest would be a leader paid mid-hole. Field-scoped matches pass
   * nothing until final.
   */
  const attested = (list: Settlement[]): Settlement[] => {
    const out: Settlement[] = []
    for (const s of list) {
      if (finalIds.has(s.matchId)) {
        out.push(s)
        continue
      }
      const m = t.matches.find((x) => x.id === s.matchId)
      if (!m || !groupScoped(m)) continue
      const unitOf = new Map<string, { groupId: string; complete: boolean }>()
      for (const tab of tabsByMatch[s.matchId] ?? [])
        for (const pid of tab.playerIds)
          unitOf.set(pid, { groupId: tab.groupId, complete: tab.complete })
      const awards = s.awards.filter((a) => {
        const u = unitOf.get(a.playerId)
        return u != null && u.complete && cardSigned(m, u.groupId)
      })
      if (awards.length > 0) out.push({ ...s, awards })
    }
    return out
  }

  /*
   * Attested AS OF THIS LINE — which is before the championship is scored,
   * and that is load-bearing in both directions. Seeding must never see the
   * championship (it is the thing being seeded), and here it cannot: the
   * final's settlement has not been pushed yet. But for the same reason this
   * list must NOT be the one the standings read — see `settledWithFinal`
   * below, and the audit finding it answers.
   */
  const settledBeforeFinal = attested(settlements)
  /*
   * The tiebreak counters obey the same gate as the points they break ties in
   * — now per card, like the points. `tabsByMatch` holds every match's tabs,
   * signed or not (the live board reads it); the counters take only the tabs
   * whose own card is signed, so two men level on settled points can never be
   * split by a birdie on a card nobody has attested to.
   */
  const settledTabs: typeof tabsByMatch = {}
  for (const [id, tabs] of Object.entries(tabsByMatch)) {
    if (finalIds.has(id)) {
      settledTabs[id] = tabs
      continue
    }
    const m = t.matches.find((x) => x.id === id)
    if (!m || !groupScoped(m)) continue
    const signedTabs = tabs.filter((tab) => tab.complete && cardSigned(m, tab.groupId))
    if (signedTabs.length > 0) settledTabs[id] = signedTabs
  }

  // Seeding is its own contest with its own tiebreak chain — the sheet separates
  // them because a seeding tie is broken on where you finished in an earlier
  // round, not on birdies.
  const seeding = computeStandings(
    activeIds,
    settledBeforeFinal,
    settledTabs,
    t.settings.seedingTiebreak,
    tb,
    matchSeq,
    'seeding',
  )
  const seeds: Record<string, number> = {}
  for (const r of seeding.rows) seeds[r.playerId] = r.position

  if (championship) score(championship, seeds)

  /*
   * Whether every card the seeding depends on has been signed for.
   *
   * The championship is excluded because it obviously cannot be signed before it
   * is played, and it is the thing being seeded. Everything else must be in.
   */
  /*
   * A round played for fun is excluded, and this is the sharpest edge on the
   * whole feature: nobody signs a card that settles nothing, so an ad-hoc round
   * left in this list would hold the seeding open for the rest of the week and
   * the championship draw could never call itself final.
   */
  const seedingComplete = t.matches
    .filter((m) => m.id !== championship?.id && matchCounts(m))
    .every((m) => finalIds.has(m.id))
  /*
   * The ledger covers everyone who earned something, not just who is still in.
   *
   * `computeStandings` silently drops an award for a player it was not handed,
   * so marking somebody inactive mid-week deleted the money he had already won
   * — while the match screens went on showing him winning it. A roster edit
   * must not rewrite what happened on Tuesday.
   *
   * Seeding above stays active-only, and correctly: the championship is
   * flighted among the men who are going to play it. It reads the same signed
   * settlements this does — see the note beside `settled`.
   */
  const earned = new Set(activeIds)
  for (const s of settlements) for (const a of s.awards) earned.add(a.playerId)

  /*
   * Filtered AGAIN, after the championship was scored, because `score(...)`
   * pushes into `settlements` as a side effect and an array filtered before
   * that push is frozen without looking frozen. The first audit that ran a
   * fully signed week through this found the whole $4,050 championship purse
   * missing from the standings — the champion's own row showed $800 while his
   * match breakdown two lines below showed the $1,500 flight award, and a
   * treasurer settling off the board would have underpaid five men.
   */
  const settledWithFinal = attested(settlements)

  const standings = computeStandings(
    [...earned],
    // The money — signed matches only, and now INCLUDING the signed final.
    settledWithFinal,
    settledTabs,
    t.settings.standingsTiebreak,
    tb,
    matchSeq,
  )

  // The board and the ledger read the SAME seeding. They used to disagree by a
  // flight, and the flight is worth a thousand dollars.
  const flights =
    championship && championship.config.kind === 'strokeNet' && championship.config.flighted
      ? assignFlights(seeding, championship.config.flights)
      : []

  /*
   * Once the final is signed for, the final decides the week.
   *
   * Up to that point the table is ranked on points, because points are what
   * decide the flights. After it, the flights have done their job and the
   * question is simply who beat the three men he was drawn against: Flight 1
   * takes 1st to 4th, Flight 2 fifth to eighth, and so on.
   */
  const champSettled = championship && finalIds.has(championship.id)
  const champAwards = champSettled
    ? (settlements.find((x) => x.matchId === championship.id)?.awards ?? [])
    : []
  const placeIn = new Map(champAwards.map((a) => [a.playerId, a.place ?? null]))
  const finalStandings =
    champSettled && flights.length > 0
      ? rankByFlightFinish(standings, flights, (pid) => placeIn.get(pid) ?? null)
      : standings

  const countedAwards = new Set<string>()
  for (const s of settledWithFinal)
    for (const a of s.awards) countedAwards.add(`${s.matchId}~${a.playerId}`)

  return {
    byMatch,
    standings: finalStandings,
    seeding,
    seedingComplete,
    flights,
    finalMatchIds: finalIds,
    isFinal,
    counted: (matchId, playerId) => countedAwards.has(`${matchId}~${playerId}`),
    signedCard: (roundId, groupId) => signed.has(`${roundId}~${groupId}`),
  }
}

/**
 * A tie the rules could not break — the coin is owed a real toss.
 *
 * The chain resolves everything it can and the engine's last resort is a flip
 * seeded off the tournament's rngSeed, which keeps the board stable between
 * refreshes. Scott's ruling is that a REAL tie for real stakes gets a real
 * coin, in front of the group: the seeded order stands in until an admin
 * performs the flip, and the recorded result (a `TiebreakResolution` in
 * `doc.tiebreaks`) then outranks the seed forever — `breakTie` consults
 * recorded resolutions first.
 */
export interface PendingFlip {
  /** 'standings' | 'seeding' | 'match:<id>' */
  scope: string
  contenders: PlayerId[]
  /** The seeded order standing in until the coin actually lands. */
  order: PlayerId[]
}

const flipKey = (scope: string, ids: readonly PlayerId[]): string =>
  `${scope}|${[...ids].sort().join(',')}`

export function pendingCoinFlips(t: Tournament, view: TournamentView): PendingFlip[] {
  const recorded = new Set(t.tiebreaks.map((r) => flipKey(r.scope, r.contenders)))
  const seen = new Set<string>()
  const out: PendingFlip[] = []
  const push = (r: { scope: string; contenders: PlayerId[]; resolvedBy: string; order: PlayerId[] }) => {
    const k = flipKey(r.scope, r.contenders)
    if (r.resolvedBy !== 'coinFlip' || recorded.has(k) || seen.has(k)) return
    seen.add(k)
    out.push({ scope: r.scope, contenders: [...r.contenders].sort(), order: [...r.order] })
  }

  /*
   * Only ties that are ABOUT something. Before the week, all twelve are level
   * on zero and the chain "resolves" them by coin — a twelve-way flip nobody
   * owes anybody. A tie is worth a ceremony once somebody in it has settled
   * points, or (for a ladder inside a match) once the flip decided an award
   * that is actually counted.
   */
  const pts = new Map(view.standings.rows.map((r) => [r.playerId, r.points]))
  /*
   * Standings and seeding flips wait for EVERY qualifying card — found in
   * review: with per-card settlement, two men can sit "dead level" on
   * Tuesday, get flipped, and then Thursday's cards hand one of them the
   * birdies that would have split them by the rules. A recorded flip
   * outranks the whole chain forever, so it must not exist until the chain
   * has had its last word. (A ladder flip inside one settled match is
   * different — that match's counters are complete — and keeps the
   * `counted` gate below.)
   */
  if (view.seedingComplete)
    for (const r of [...view.standings.tiebreaks, ...view.seeding.tiebreaks])
      if (r.contenders.some((id) => (pts.get(id) ?? 0) > 0)) push(r)
  for (const { match, settlement } of Object.values(view.byMatch))
    if (matchCounts(match))
      for (const r of settlement.tiebreaks)
        if (r.contenders.some((id) => view.counted(match.id, id))) push(r)
  return out
}

/**
 * "Play the week out" — Scott's simulator, 2026-08-19.
 *
 * The reader picks results for everything not yet settled and this projects
 * where the points land, most importantly which FLIGHT each man falls in.
 * Nothing here is stored or shared: it is one phone's guess, layered on top
 * of the points that are already real.
 *
 * The projection is points-only, deliberately. Simulated golf has no cards,
 * so the birdie-count tiebreak chain has nothing to read — projected ties
 * keep the current seeding order and the panel says the flights are a guess.
 * The championship itself is never simulated: it is the thing the flights
 * exist to seed.
 */
export interface SimPicks {
  /** pairing id → which side takes it, or a halve. */
  contests: Record<string, 'a' | 'b' | 'half'>
  /**
   * Predicted finishing order, first place first. Keyed `matchId` for a
   * field-ranked quota, `matchId~groupId` for a group-ranked one.
   */
  quota: Record<string, PlayerId[]>
}

export const EMPTY_PICKS: SimPicks = { contests: {}, quota: {} }

export interface SimRow {
  playerId: PlayerId
  /** Points already settled — the real ones. */
  settled: number
  /** Points the picks would add. */
  simmed: number
  position: number
}

export function simulateSeeding(
  t: Tournament,
  view: TournamentView,
  picks: SimPicks,
): { rows: SimRow[]; flights: { name: string; playerIds: PlayerId[] }[] } {
  const settled = new Map<PlayerId, number>()
  const seedPos = new Map<PlayerId, number>()
  for (const r of view.seeding.rows) {
    settled.set(r.playerId, r.points)
    seedPos.set(r.playerId, r.position)
  }
  const simmed = new Map<PlayerId, number>()
  const add = (pid: PlayerId, pts: number) => simmed.set(pid, (simmed.get(pid) ?? 0) + pts)

  const championship = t.matches.find(
    (m) => m.config.kind === 'strokeNet' && m.config.flighted,
  )

  for (const m of t.matches) {
    if (!matchCounts(m) || m.id === championship?.id) continue
    const c = m.config

    if (c.kind === 'quota') {
      const blocks =
        c.rankingScope === 'group'
          ? t.groups
              .filter((g) => g.roundId === m.roundId)
              .map((g) => ({ key: `${m.id}~${g.id}`, valid: new Set(g.playerIds) }))
          : [
              {
                key: m.id,
                valid: new Set(
                  t.groups.filter((g) => g.roundId === m.roundId).flatMap((g) => g.playerIds),
                ),
              },
            ]
      for (const b of blocks) {
        /*
         * Stale picks filtered against TODAY's draw, exactly as the screen
         * filters them — found in review: a pick order saved before a redraw
         * silently paid first place to a man no longer in the foursome and
         * pushed everybody real down a rung.
         */
        const order = (picks.quota[b.key] ?? []).filter((pid) => b.valid.has(pid))
        order.forEach((pid, i) => {
          // A settled award is real; the pick defers to it.
          if (view.counted(m.id, pid)) return
          add(pid, payoutForPlace(c.payouts, i + 1)?.points ?? 0)
        })
      }
      continue
    }

    if (c.kind === 'strokeNet') continue

    for (const pairing of t.pairings.filter((x) => x.matchId === m.id)) {
      const everyone = pairing.sides.flatMap((sd) => sd.playerIds)
      // A contest settles whole, so one counted man means the result is real.
      if (everyone.some((pid) => view.counted(m.id, pid))) continue
      const pick = picks.contests[pairing.id]
      if (!pick) continue
      if (pick === 'half') {
        for (const pid of everyone) add(pid, c.payout.halve.points)
      } else {
        const winners = pairing.sides[pick === 'a' ? 0 : 1]!.playerIds
        for (const pid of winners) add(pid, c.payout.win.points)
        for (const pid of everyone)
          if (!winners.includes(pid)) add(pid, c.payout.loss.points)
      }
    }
  }

  const rows = t.players
    .filter((p) => p.active)
    .map((p) => ({
      playerId: p.id,
      settled: settled.get(p.id) ?? 0,
      simmed: simmed.get(p.id) ?? 0,
    }))
    .sort((a, b) => {
      const d = b.settled + b.simmed - (a.settled + a.simmed)
      // Projected ties keep today's seeding order — simulated golf has no
      // cards, so the counter chain has nothing truthful to say about it.
      return d !== 0 ? d : (seedPos.get(a.playerId) ?? 99) - (seedPos.get(b.playerId) ?? 99)
    })
    .map((r, i) => ({ ...r, position: i + 1 }))

  const flights =
    championship?.config.kind === 'strokeNet'
      ? championship.config.flights.map((f) => ({
          name: f.name,
          playerIds: rows
            .filter((r) => r.position >= f.fromSeed && r.position <= f.toSeed)
            .map((r) => r.playerId),
        }))
      : []

  return { rows, flights }
}

/**
 * One concrete road to Flight 1 — "Map my path", Scott's ask.
 *
 * Not an optimizer and not a lecture: it proposes at most two stories and
 * takes the first that works. "Held serve" is you winning everything still
 * open to you — your contests, first in your quota groups — while every
 * other match halves and every other quota ladder pays in the order the
 * seeding already stands. If that is not enough, "gone cold" keeps your wins
 * and reverses the other ladders, so the men above you eat the bottom of
 * every table. If even THAT leaves you outside the flight, there is no path
 * on results alone, and the honest answer is the seed you would still be.
 *
 * The scenario comes back as ordinary simulator picks, so the reader can see
 * it, argue with it, and tap it apart.
 */
/**
 * Every man's road into Flight 1, at once.
 *
 * `pathToFlightOne` answers "can I get there" for one man by building a
 * road; this answers "who is still alive" for the whole field, by
 * arithmetic rather than simulation — which is what makes it honest enough
 * to say ELIMINATED out loud.
 *
 * A man's FLOOR is what he has now: no format in this week takes points
 * back. His CEILING is that plus the best he could take from every match
 * still open to him. He is eliminated when four men have floors above his
 * ceiling, because those four fill the flight without him whatever he does.
 * Everything softer than that is a judgment about how much has to go right,
 * and it is stated as a gap rather than a promise.
 */
export interface FlightOutlookRow {
  playerId: PlayerId
  position: number
  points: number
  ceiling: number
  /** Points still available to him across the unplayed counted matches. */
  upside: number
  /** Points he must gain ON the man holding the last seat. */
  gapToCut: number
  status: 'in' | 'live' | 'help' | 'miracle' | 'out'
  /** Men whose floor already exceeds his ceiling. */
  blockers: PlayerId[]
}

export function flightOutlook(
  t: Tournament,
  view: TournamentView,
): { rows: FlightOutlookRow[]; seats: number; upsideLeft: number } {
  const champ = t.matches.find((m) => m.config.kind === 'strokeNet' && m.config.flighted)
  const seats = champ?.config.kind === 'strokeNet' ? (champ.config.flights[0]?.toSeed ?? 4) : 4

  /*
   * The best a man can still take. A match he has already been paid for is
   * spent for him even while other foursomes finish it, which is why this
   * asks `counted` per player rather than trusting the match to be open.
   */
  const upsideFor = (pid: PlayerId): number => {
    let n = 0
    for (const m of t.matches) {
      if (!matchCounts(m) || m.id === champ?.id) continue
      if (view.counted(m.id, pid)) continue
      const inIt = t.groups.some((g) => g.roundId === m.roundId && g.playerIds.includes(pid))
      if (!inIt) continue
      /*
       * Head-to-head formats pay a win; ranked formats pay a ladder. Both
       * shapes appear in one week, so the best case has to be read from
       * whichever the match actually uses.
       */
      const c = m.config
      n +=
        'payouts' in c
          ? Math.max(0, ...c.payouts.map((x) => x.points))
          : 'payout' in c
            ? c.payout.win.points
            : 0
    }
    return n
  }

  const base = view.seeding.rows.map((r) => ({
    playerId: r.playerId,
    position: r.position,
    points: r.points,
    upside: upsideFor(r.playerId),
  }))
  const cut = base[seats - 1]?.points ?? 0

  const rows: FlightOutlookRow[] = base.map((r) => {
    const ceiling = r.points + r.upside
    const blockers = base.filter((x) => x.playerId !== r.playerId && x.points > ceiling)
    const gapToCut = Math.max(0, cut + 1 - r.points)
    const status: FlightOutlookRow['status'] =
      blockers.length >= seats
        ? 'out'
        : r.position <= seats
          ? 'in'
          : gapToCut > r.upside
            ? 'out'
            : gapToCut >= r.upside
              ? 'miracle'
              : gapToCut * 2 > r.upside
                ? 'help'
                : 'live'
    return { ...r, ceiling, gapToCut, status, blockers: blockers.map((x) => x.playerId) }
  })

  return { rows, seats, upsideLeft: Math.max(0, ...rows.map((r) => r.upside)) }
}

export function pathToFlightOne(
  t: Tournament,
  view: TournamentView,
  me: PlayerId,
): { picks: SimPicks; position: number; flavor: 'held' | 'cold' } | { position: number } {
  const championship = t.matches.find(
    (m) => m.config.kind === 'strokeNet' && m.config.flighted,
  )
  const top =
    championship?.config.kind === 'strokeNet'
      ? (championship.config.flights[0]?.toSeed ?? 4)
      : 4
  const standing = new Map(view.seeding.rows.map((r) => [r.playerId, r.position]))
  const rank = (pid: PlayerId) => standing.get(pid) ?? 99

  const build = (flavor: 'held' | 'cold'): SimPicks => {
    const picks: SimPicks = { contests: {}, quota: {} }
    for (const m of t.matches) {
      if (!matchCounts(m) || m.id === championship?.id) continue
      const c = m.config
      if (c.kind === 'quota') {
        const blocks =
          c.rankingScope === 'group'
            ? t.groups
                .filter((g) => g.roundId === m.roundId)
                .map((g) => ({ key: `${m.id}~${g.id}`, playerIds: g.playerIds }))
            : [
                {
                  key: m.id,
                  playerIds: t.groups
                    .filter((g) => g.roundId === m.roundId)
                    .flatMap((g) => g.playerIds),
                },
              ]
        for (const b of blocks) {
          const others = b.playerIds
            .filter((pid) => pid !== me)
            .sort((x, y) => (flavor === 'held' ? rank(x) - rank(y) : rank(y) - rank(x)))
          picks.quota[b.key] = b.playerIds.includes(me) ? [me, ...others] : others
        }
        continue
      }
      if (c.kind === 'strokeNet') continue
      for (const pairing of t.pairings.filter((x) => x.matchId === m.id)) {
        const everyone = pairing.sides.flatMap((sd) => sd.playerIds)
        if (everyone.some((pid) => view.counted(m.id, pid))) continue
        const mine = pairing.sides.findIndex((sd) => sd.playerIds.includes(me))
        picks.contests[pairing.id] = mine === 0 ? 'a' : mine === 1 ? 'b' : 'half'
      }
    }
    return picks
  }

  for (const flavor of ['held', 'cold'] as const) {
    const picks = build(flavor)
    const sim = simulateSeeding(t, view, picks)
    const position = sim.rows.find((r) => r.playerId === me)?.position ?? 99
    if (position <= top) return { picks, position, flavor }
    if (flavor === 'cold') return { position }
  }
  /* istanbul ignore next -- the loop always returns */
  return { position: 99 }
}

export const playerName = (t: Tournament | null, id: string): string =>
  t?.players.find((p) => p.id === id)?.displayName ?? id

export interface RoundNet {
  /** Holes with a ball posted, including pick-ups. */
  thru: number
  /** Net strokes over the holes that produced a usable net. */
  net: number
  /** Those same holes' net measured against their par. */
  toPar: number
}

/**
 * A player's running net for one round.
 *
 * Pops come from the round's own allocation rather than the full handicap, so
 * this number always agrees with the POP badges sitting next to it on the card:
 * relative to the low man in a match, full handicap in quota and the final.
 * Showing one basis beside badges drawn from another is how you get a player
 * arguing with the screen on the 7th tee.
 *
 * A hole that was conceded, or picked up without a most-likely score, has no
 * usable net. It still counts toward `thru` — it was played — but contributes
 * to neither side of `toPar`, because inventing a number for it would quietly
 * change the total.
 */
export function roundNet(ctx: RoundContext, playerId: string): RoundNet | null {
  const values = ctx.scores[playerId]
  if (!values) return null

  let net = 0
  let par = 0
  let thru = 0
  ctx.holes.forEach((hole, i) => {
    const ball = ballFor(values[i], ctx.pops[playerId]?.[i] ?? 0)
    if (ball.pending) return
    thru += 1
    if (ball.net == null) return
    net += ball.net
    par += hole.par
  })

  return thru === 0 ? null : { thru, net, toPar: net - par }
}

/**
 * The pops a player still has coming in a contest that is still alive.
 *
 * "Have I got one left" is asked mid-match, standing on a tee, and the card
 * answers it slowly: you have to find your ring holes and remember which are
 * behind you. This is that count — pops on holes the match has not settled,
 * minus any hole the man himself has already played (his pop there is in his
 * score, not still coming).
 *
 * Zero once the match is decided, however many stroke holes are left on the
 * card: a pop behind a closeout is a pop on holes nobody is playing for
 * anything, and advertising it would read as hope.
 */
export function popsRemaining(tab: UnitTab, playerId: string): number {
  if (tab.complete) return 0
  if (tab.detail.kind === 'matchPlay' && tab.detail.closedOut) return 0
  return tab.perHole.reduce(
    (sum, h) =>
      h.outcome == null && h.gross[playerId] == null ? sum + (h.pops[playerId] ?? 0) : sum,
    0,
  )
}

/** Golf's own shorthand: level par is E, never 0. */
export const toParLabel = (toPar: number): string =>
  toPar === 0 ? 'E' : `${toPar > 0 ? '+' : ''}${toPar}`

/**
 * "Mon 24 Aug" from a round's date.
 *
 * The day is derived, never stored. A label that says Monday is a second copy
 * of the same fact, and the copy is what goes stale the moment the schedule
 * moves — which it does, every year.
 *
 * Parsed field by field rather than through `new Date(iso)`: that treats a bare
 * YYYY-MM-DD as UTC midnight, which renders as the previous day for anyone west
 * of Greenwich. Bandon is eight hours west, so it would be wrong all week.
 */
export function roundDay(date: string | undefined): string {
  if (!date) return ''
  const [y, m, d] = date.split('-').map(Number)
  if (!y || !m || !d) return ''
  const local = new Date(y, m - 1, d)
  if (Number.isNaN(local.getTime())) return ''
  return local.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

/**
 * The week in the order it is played, renumbered as it goes.
 *
 * `seq` is not decoration: the day sections read it to order two rounds inside
 * one day, `currentRoundId` walks it to decide where a scorer lands, and the
 * tiebreak rules talk about "the later round". So sorting a list for display
 * while leaving `seq` saying something else would put Monday afternoon above
 * Monday morning everywhere except the screen that had been sorted.
 *
 * Date first, then the tee time — a round that goes off later in the day is
 * later in the week. A tie is left in the order it arrived, because two rounds
 * off the same tee at the same minute is an unfinished document rather than a
 * question this should invent an answer to.
 */
export function inPlayOrder<R extends { date?: string; firstTeeTime?: string; seq: number }>(
  rounds: readonly R[],
): R[] {
  return [...rounds]
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const key = (x: R) => `${x.date ?? ''} ${x.firstTeeTime ?? ''}`
      return key(a.r) < key(b.r) ? -1 : key(a.r) > key(b.r) ? 1 : a.i - b.i
    })
    .map(({ r }, i) => ({ ...r, seq: i + 1 }))
}

/**
 * Every match, in the order the week plays them.
 *
 * Play order is the DAY and the TEE TIME, not the stored `seq`. Two documents
 * disagree with their own seq routinely: a round added on a Tuesday evening
 * takes the next number after Thursday's championship, and any edit made
 * outside Setup can leave the field behind. Reading the date means the list is
 * right whether or not anybody has renumbered.
 *
 * A match whose round has been deleted is dropped rather than floated to one
 * end: it cannot be scored, carded or paid, and every caller here is building a
 * list for a human.
 */
export function matchesInPlayOrder<M extends { roundId: string; seq: number }>(doc: {
  rounds: readonly { id: string; seq: number; date?: string; firstTeeTime?: string }[]
  matches: readonly M[]
}): M[] {
  // One definition of play order, shared with the round list itself.
  const at = new Map(inPlayOrder(doc.rounds).map((r) => [r.id, r.seq]))
  return doc.matches
    .filter((m) => at.has(m.roundId))
    .sort((a, b) => at.get(a.roundId)! - at.get(b.roundId)! || a.seq - b.seq)
}

/**
 * The weekday on its own — "Mon".
 *
 * Not `roundDay(...).split(',')[0]`, which is what this replaced: only en-US
 * puts a comma after the weekday, so under en-GB every round in every picker
 * read as the whole date.
 */
export function weekdayOf(date: string | undefined): string {
  if (!date) return ''
  const [y, m, d] = date.split('-').map(Number)
  if (!y || !m || !d) return ''
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short' })
}

/**
 * How a round reads in a picker, on Score and in the clubhouse grid.
 *
 * The label alone is not an identity. "Bandon Dunes — front" is two different
 * matches this week, and an eighteen added from a tee is labeled "Bandon
 * Dunes" — byte-identical to the scheduled Monday round. A man entering four
 * paper cards picks one of two identical lines and writes a foursome's scores
 * into the wrong round.
 *
 * So: the day, then the match, which carries the number, the course and the
 * format. And whether it pays anything, which the name cannot say and which
 * changes what the scores mean.
 */
export function roundPickerLabel(
  doc: Pick<Tournament, 'rounds' | 'courses' | 'matches'>,
  round: Round,
): string {
  const match = doc.matches.find((m) => m.roundId === round.id)
  const day = weekdayOf(round.date)
  const name = match ? matchNameIn(doc, match) : round.label
  const tag = match && !matchCounts(match) ? ' — for fun' : ''
  return `${day === '' ? '' : `${day} · `}${name}${tag}`
}

/**
 * The week, cut into days.
 *
 * Four days of golf, nine rounds, and until now one unbroken scroll — so the
 * week view groups by the date the rounds share and shows one day at a time.
 *
 * An undated round is a half-built one, so it sorts last under its own blank
 * heading: keyed on the empty string it would otherwise file ahead of the whole
 * week, since '' sorts before every real date.
 */
export function roundDays<R extends { seq: number; date?: string }>(
  rounds: readonly R[],
): { date: string; day: string; rounds: R[] }[] {
  const byDate = new Map<string, R[]>()
  for (const round of rounds) {
    const date = round.date ?? ''
    const list = byDate.get(date) ?? []
    list.push(round)
    byDate.set(date, list)
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a < b ? -1 : a > b ? 1 : 0))
    .map(([date, list]) => ({
      date,
      day: roundDay(date),
      rounds: [...list].sort((a, b) => a.seq - b.seq),
    }))
}

/**
 * The one day the week view opens on.
 *
 * Exactly one section is open, so this is the whole answer to "what does the
 * screen say when you land on it" — and it has to be right on the days that are
 * not today, which is most of the year. Today if there is golf today, otherwise
 * the next day of golf, otherwise the last day of the week.
 *
 * Those fallbacks are the same shape as `currentRoundId`'s and for the same
 * reason: in the fortnight before the trip this screen is read to find out who
 * you are off with on the first tee, and in September it is read to find out
 * what happened in the championship. Neither reader wants an empty page, and
 * the September one does not want Sunday.
 *
 * The undated section is never chosen while a real day exists — it is where a
 * half-built round waits, not a day of the week.
 *
 * Null only when there is nothing to open. `today` is injected because a
 * function that reads the clock cannot be tested.
 */
export function currentDay(
  days: readonly { date: string }[],
  today: string,
): string | null {
  if (days.length === 0) return null
  const dated = days.filter((d) => d.date !== '')
  if (dated.length === 0) return days[0]!.date
  const ahead = dated.find((d) => d.date >= today)
  return (ahead ?? dated[dated.length - 1]!).date
}

/**
 * The days the week view opens with — today and everything still to come.
 *
 * The accordion used to be a single value, which made Tuesday night's question
 * ("what time Wednesday, and who Thursday?") a tap per day. Now a day folds
 * only once it is BEHIND you: before the trip the whole week is open, and each
 * morning the previous day's golf collapses to a header on its own.
 *
 * The fallbacks are `currentDay`'s, for `currentDay`'s reasons: once the week
 * is over the last day stays open, because the September reader wants the
 * championship and not four folded headers; and the undated section — where a
 * half-built round waits — opens only when it is all there is.
 */
export function defaultOpenDays(
  days: readonly { date: string }[],
  today: string,
): string[] {
  if (days.length === 0) return []
  const dated = days.filter((d) => d.date !== '')
  if (dated.length === 0) return [days[0]!.date]
  const ahead = dated.filter((d) => d.date >= today).map((d) => d.date)
  return ahead.length > 0 ? ahead : [dated[dated.length - 1]!.date]
}

/**
 * The round somebody standing on a tee is most likely to be scoring.
 *
 * Every screen that picks a round opened on `rounds[0]` — so at five o'clock on
 * Sunday, on the 10th tee for the back nine, the Score tab said "Bandon Dunes —
 * front, Hole 1" and pre-selected your real foursome. The first `+` writes a
 * par into a card you signed an hour ago; the first `−` writes a birdie and
 * buzzes twelve phones. Nothing else in the app was date-aware at all.
 *
 * Today's first UNFINISHED round, then today's last round, then the nearest day
 * in the future, then the last round of the week. The fallbacks matter as much
 * as the rule: on the 22nd it opens on Sunday's first nine, and on the 28th it
 * opens on the Championship rather than snapping back to Sunday.
 *
 * `today` is injected rather than read, because a function that reads the clock
 * cannot be tested and this one decides where eleven people start typing.
 */
export function currentRoundId(
  t: Pick<Tournament, 'rounds'>,
  isComplete: (roundId: string) => boolean,
  today: string,
): string {
  const ordered = [...t.rounds].sort((a, b) => a.seq - b.seq)
  if (ordered.length === 0) return ''

  const todays = ordered.filter((r) => r.date === today)
  const unfinished = todays.find((r) => !isComplete(r.id))
  if (unfinished) return unfinished.id
  // Every round today is in: stay on the last one rather than jumping to
  // tomorrow, because the reason to open Score now is to fix something.
  if (todays.length > 0) return todays[todays.length - 1]!.id

  const ahead = ordered.find((r) => (r.date ?? '') > today)
  return (ahead ?? ordered[ordered.length - 1]!).id
}

/*
 * Where a screen was left, remembered across a remount.
 *
 * `App` renders one tab at a time, so every other tab is UNMOUNTED rather than
 * hidden, and a `useState` default runs again on every return. For the Score tab
 * that silently snapped the round back to the morning nine while you were halfway
 * through the afternoon one — worse with a date-aware default than a fixed one,
 * because it looks like the app deciding rather than forgetting.
 *
 * Module scope rather than the store: these are cursors on a screen, not
 * something to sync, persist or put in a URL, and forgetting them when the app is
 * closed is correct.
 *
 * ONLY reach for this when no surviving parent can own the value. The Cards tab
 * had two cursors here and both were wrong: its sub-screen and its chosen match
 * are navigation, `App` already owns the rest of the navigation AND never
 * unmounts, so lifting them there kept the place for free and — the actual bug —
 * stopped a remembered value outranking an explicit tap. A birdie in the feed
 * opened "The week" because a cursor remembered it last. `App` cannot pass this
 * round down to Score the same way, because Score owns which round it is showing
 * and nothing above it needs to know, so the round genuinely belongs here.
 */
const cursors = new Map<string, string>()
export const remember = (key: string, value: string): void => {
  cursors.set(key, value)
}
export const recalled = (key: string): string | null => cursors.get(key) ?? null

/** The round the scorer last had open. */
export const rememberRound = (id: string): void => remember('round', id)
export const recalledRound = (): string | null => recalled('round')

/*
 * A one-shot jump into the scorer: this foursome, this hole.
 *
 * Set by a tap on a card cell — "that 6 is wrong, fix it" — and CONSUMED on
 * read, because it is an errand rather than a cursor: once Score has landed
 * on the hole, the next visit to the tab must open wherever the scorer was,
 * not wherever the last complaint pointed.
 */
let scoreTarget: { groupId: string; holeNo: number } | null = null
export const rememberScoreTarget = (t: { groupId: string; holeNo: number }): void => {
  scoreTarget = t
}
export const takeScoreTarget = (): { groupId: string; holeNo: number } | null => {
  const t = scoreTarget
  scoreTarget = null
  return t
}

// ---------------------------------------------------------------------------
// Cards nobody signed for
// ---------------------------------------------------------------------------

/** One foursome's card that has been played on and not signed for. */
export interface OutstandingCard {
  roundId: string
  roundLabel: string
  /** The round's date, `YYYY-MM-DD`. Empty if the round carries none. */
  date: string
  groupId: string
  /** 1-based, as spoken: "group 2", not "group 1" for the second one out. */
  groupNo: number
  teeTime: string
  playerIds: string[]
  /** Holes with a live score, across every player in the group. */
  holesIn: number
  /**
   * Whole days between the round's date and today. 0 while it is still that day,
   * 1 once it has rolled over — which is the whole point of the list.
   */
  daysLate: number
}

/**
 * Cards that were played on and never signed for.
 *
 * Signing is what releases money: `computeAll` will not settle a match until
 * EVERY group in its round has signed, so one foursome that walks off without
 * signing silently holds up three other men's payout — and does it quietly,
 * because the live board keeps showing scores the entire time. Nothing on any
 * screen said whose signature was missing.
 *
 * Two rules keep this from crying wolf, and both matter more than the list:
 *
 *   - A round nobody has touched is NOT an unsigned card. Every future round in
 *     the week has groups and no scores, so counting those would put nine
 *     permanent warnings on the screen from the moment the draw is made, and a
 *     warning that is always on is one nobody reads by Tuesday.
 *   - `daysLate` is computed from the round's own date, not from "is this the
 *     current round". The nag people actually need is the one for yesterday,
 *     while the group is at breakfast and can still fix it.
 *
 * Derived on every render from the same document, scores and confirmations the
 * rest of the app reads. No table, no queue, no scheduled job: a signature
 * arriving makes its row disappear on the next poll, and a score corrected into
 * a round that was already signed cannot resurrect one.
 */
export function outstandingCards(
  t: Tournament,
  scores: HoleScore[],
  confirmed: { roundId: string; groupId: string }[],
  /** `YYYY-MM-DD` in the tournament's own day, not UTC. */
  today: string,
): OutstandingCard[] {
  const signed = new Set(confirmed.map((c) => `${c.roundId}~${c.groupId}`))

  // Holes in, per round and player, counted once rather than per group.
  const played = new Map<string, number>()
  for (const s of scores) {
    const key = `${s.roundId}~${s.playerId}`
    played.set(key, (played.get(key) ?? 0) + 1)
  }

  const out: OutstandingCard[] = []
  for (const round of t.rounds) {
    /*
     * A round played for fun is never chased. Signing is what turns a running
     * total into money, and there is no money here — so a nag that cannot be
     * satisfied would sit on the list all week teaching everyone to ignore it,
     * which is the one thing this list cannot afford.
     */
    if (t.matches.some((m) => m.roundId === round.id && !matchCounts(m))) continue
    for (const group of t.groups.filter((g) => g.roundId === round.id)) {
      if (signed.has(`${round.id}~${group.id}`)) continue
      const holesIn = group.playerIds.reduce(
        (n, p) => n + (played.get(`${round.id}~${p}`) ?? 0),
        0,
      )
      if (holesIn === 0) continue
      out.push({
        roundId: round.id,
        roundLabel: round.label,
        date: round.date ?? '',
        groupId: group.id,
        groupNo: group.index + 1,
        teeTime: group.teeTime,
        playerIds: group.playerIds,
        holesIn,
        daysLate: round.date ? daysBetween(round.date, today) : 0,
      })
    }
  }

  // Oldest first: the one holding up money the longest is the one to chase.
  return out.sort((a, b) => a.date.localeCompare(b.date) || a.groupNo - b.groupNo)
}

/**
 * Today, as `YYYY-MM-DD` in the phone's own timezone.
 *
 * `en-CA` is the shortest way to an ISO date that is LOCAL. `toISOString()` is
 * the trap here: it is UTC, so from 5pm on the Oregon coast it already reads
 * tomorrow — which would call every card of the current round a day late,
 * every evening, exactly when people are signing them.
 */
export const todayLocal = (): string => new Date().toLocaleDateString('en-CA')

/**
 * Whole days from `from` to `to`, both `YYYY-MM-DD`.
 *
 * Parsed as UTC noon rather than midnight so a daylight-saving boundary inside
 * the range cannot shift the difference by an hour and round the wrong way.
 * Negative when the round is still ahead, which is why callers filter on > 0.
 */
function daysBetween(from: string, to: string): number {
  const at = (d: string): number => Date.parse(`${d}T12:00:00Z`)
  const a = at(from)
  const b = at(to)
  if (Number.isNaN(a) || Number.isNaN(b)) return 0
  return Math.round((b - a) / 86_400_000)
}

/**
 * The shortest name that still tells the foursome apart, for columns that have
 * no room for words.
 *
 * First and last initial — "Scott Cramer" signs as SC — EXCEPT where two men in
 * the same set collide, which this roster genuinely does twice over: Tyler and
 * Thomas Godfrey are both TG, and Scott Perucca and Shawn Perucca are both SP.
 * A sign-off sheet where half the rows share a label is worse than one that
 * scrolls, so colliding labels take letters from the first name until they
 * split: Ty.G and Th.G, Sc.P and Sh.P.
 *
 * Computed over the set being shown, not the roster, so the label is as short
 * as THIS table allows — a foursome with no collision gets clean initials even
 * though the full roster has two. Falls back to the display name when a man has
 * no usable full name, because an empty label is the one output worse than a
 * long one.
 */
export function shortNames(
  players: { id: string; fullName?: string; displayName: string }[],
): Map<string, string> {
  const parts = players.map((p) => {
    const words = (p.fullName ?? '').trim().split(/\s+/).filter(Boolean)
    if (words.length < 2) return { id: p.id, first: null, last: null, fallback: p.displayName }
    return { id: p.id, first: words[0]!, last: words[words.length - 1]!, fallback: p.displayName }
  })

  const label = (x: (typeof parts)[number], firstLen: number): string =>
    x.first === null || x.last === null
      ? x.fallback
      : firstLen === 1
        ? `${x.first[0]!.toUpperCase()}${x.last[0]!.toUpperCase()}`
        : `${x.first.slice(0, firstLen)}.${x.last[0]!.toUpperCase()}`

  const out = new Map<string, string>()
  const firstLen = new Map<string, number>(parts.map((x) => [x.id, 1]))

  // Widen only the colliding labels, and only until they part. Bounded by the
  // length of the longest first name, after which identical labels are two men
  // the letters genuinely cannot separate — full display names, then.
  for (let pass = 0; pass < 12; pass += 1) {
    const seen = new Map<string, string[]>()
    for (const x of parts) {
      const l = label(x, firstLen.get(x.id)!)
      seen.set(l, [...(seen.get(l) ?? []), x.id])
    }
    const clashing = [...seen.values()].filter((ids) => ids.length > 1).flat()
    if (clashing.length === 0) break
    let widened = false
    for (const id of clashing) {
      const x = parts.find((p) => p.id === id)!
      if (x.first !== null && firstLen.get(id)! < x.first.length) {
        firstLen.set(id, firstLen.get(id)! + 1)
        widened = true
      }
    }
    if (!widened) {
      for (const id of clashing) {
        const x = parts.find((p) => p.id === id)!
        out.set(id, x.fallback)
      }
      break
    }
  }

  for (const x of parts) {
    if (!out.has(x.id)) out.set(x.id, label(x, firstLen.get(x.id)!))
  }
  return out
}

/**
 * The championship's groups, with Flight N's men written into the group
 * STAMPED flight N. Everything else about every group — id, tee time, slot —
 * is untouched, and the ids being untouched is the point: signatures and
 * settlements key on roundId~groupId, so membership must change in place or
 * signed money comes loose.
 *
 * Matched on the stamp rather than the row position because the admin may have
 * reordered tee times — "Flight 1 goes off last" has to survive this write.
 * A group with no stamp falls back to its position among its round's groups,
 * which is only ever exercised by draws older than the stamp.
 */
export function fillFlightGroups(
  t: Tournament,
  roundId: string,
  flights: { flight: { name: string }; playerIds: string[] }[],
): Tournament['groups'] {
  const inRound = t.groups.filter((g) => g.roundId === roundId)
  return t.groups.map((g) => {
    if (g.roundId !== roundId) return g
    const flightNo = g.flight ?? inRound.indexOf(g) + 1
    const flight = flights[flightNo - 1]
    if (!flight) return g
    return { ...g, playerIds: [...flight.playerIds] }
  })
}

/**
 * A foursome in the order the golf is played, and where the rules fall.
 *
 * The draw stores a group as it was drawn — Springer, Phil, Poulton, Thomas —
 * while the matches inside it are Springer v Poulton and Phil v Thomas. Reading
 * down the drawn order puts a stranger between every pair of opponents, which
 * on the scorer's card means the two numbers that answer each other are never
 * adjacent, and on the week's pop tables meant "gives 1 / 1 on 5" stopped
 * lining up as a pair.
 *
 * Two kinds of boundary come back, because they are different facts and the
 * screens draw them differently. `contestStarts` opens a new MATCH — the men
 * above it are playing nobody below it. `sideStarts` opens the other side of
 * the match you are already in, which is the "v" in a 2v2. A foursome of two
 * singles has one contest boundary; a 2v2 has one side boundary; a quota round
 * has neither and keeps the drawn order, because inventing a contest there
 * would tell a man he is playing somebody he is not.
 *
 * Anybody the pairings do not mention trails the ones they do, rather than
 * being dropped: a five-man group and a man cut from a contest after the draw
 * both still have a card, and a card with a missing row is worse than one in an
 * odd order. Pairings naming men outside this group are ignored for the same
 * reason in reverse — a stale pairing left by a redraw must not inject a
 * stranger into a foursome.
 */
export interface ContestOrder {
  /** Every man in the group, opponents adjacent. */
  playerIds: string[]
  /** Row indexes that open a new contest. Never contains 0. */
  contestStarts: Set<number>
  /** Row indexes that open the far side of the contest above. */
  sideStarts: Set<number>
  /** The contests themselves, in pairing order, for screens that name them. */
  contests: { pairing: Pairing; playerIds: string[] }[]
}

export function contestOrder(
  groupPlayerIds: readonly string[],
  pairings: readonly Pairing[],
): ContestOrder {
  const inGroup = (id: string) => groupPlayerIds.includes(id)
  const contests = [...pairings]
    .sort((a, b) => a.index - b.index)
    .map((p) => ({
      pairing: p,
      sides: p.sides.map((s) => s.playerIds.filter(inGroup)),
    }))
    // A pairing none of whose men are here belongs to another foursome.
    .filter((c) => c.sides.some((s) => s.length > 0))

  const playerIds: string[] = []
  const contestStarts = new Set<number>()
  const sideStarts = new Set<number>()

  for (const c of contests) {
    if (playerIds.length > 0) contestStarts.add(playerIds.length)
    c.sides.forEach((s, i) => {
      if (i > 0 && s.length > 0 && playerIds.length > 0) sideStarts.add(playerIds.length)
      playerIds.push(...s)
    })
  }

  // Whoever the draw has and the pairings do not.
  playerIds.push(...groupPlayerIds.filter((id) => !playerIds.includes(id)))

  return {
    playerIds,
    contestStarts,
    sideStarts,
    contests: contests.map((c) => ({ pairing: c.pairing, playerIds: c.sides.flat() })),
  }
}
